import Axios, { CancelToken, CancelTokenSource } from 'axios';
import { DependencyList, Dispatch, EffectCallback, useEffect, useRef, useState } from 'react';

import { getStorageItem, keys, setStorageItem } from './localStorageAccessor';
import { ERROR_ID, GENERIC_ERROR_KEY, showNotificationWithTimeout } from '@shared/redux/actions/notification-actions';
import { useAppDispatch } from '@shared/redux/hooks';
import { NotificationKinds } from '@shared/redux/state/notification';

type AsyncEffectParams = {
  callback: (ct: CancelToken) => Promise<void>;
  catchCallback?: (e: any) => void;
  finallyCallback?: () => void;
  showGenericError?: boolean;
};

export type MountState = {
  isMounted: boolean;
};

export const useEffectAsync = (
  { callback, catchCallback, finallyCallback, showGenericError }: AsyncEffectParams,
  deps?: DependencyList,
) => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    const cts: CancelTokenSource = Axios.CancelToken.source();
    const callbackWrapper = async () => {
      try {
        await callback(cts.token);
      } catch (e) {
        if (!Axios.isCancel(e)) {
          catchCallback?.(e);
          if (showGenericError) {
            dispatch(
              showNotificationWithTimeout({
                id: ERROR_ID,
                resourceKey: GENERIC_ERROR_KEY,
                kind: NotificationKinds.Warning,
              }),
            );
          }
        }
      } finally {
        finallyCallback?.();
      }
    };
    callbackWrapper();
    return cts.cancel;
  }, deps);
};

export const useDidUpdateEffect = (func: EffectCallback, deps?: DependencyList) => {
  const didComponentMount = useRef(false);

  useEffect(() => {
    if (!didComponentMount.current) {
      didComponentMount.current = true;
    } else {
      func();
    }
  }, deps);
};

export const useAsyncUpdate = <T, TArgs extends Array<any>>(
  callback: (mountState: MountState, ...args: TArgs) => Promise<T>,
) => {
  const objectMounted = useRef<MountState>({ isMounted: true });

  useEffect(() => {
    // looks like it is bug or whatever in react/jest, in jest test
    // effect is called and objectMounted.current is null (it should not be in all cases).
    if (objectMounted.current) {
      objectMounted.current.isMounted = true;
      return () => {
        objectMounted.current.isMounted = false;
      };
    }
  }, []);

  return (...args: TArgs) => callback(objectMounted.current, ...args);
};

export const useLocalStorageState = <T>(name: keyof typeof keys, getDefaultValue: () => T) => {
  const savedValue = getStorageItem<T>(name);
  const defaultValue = savedValue ?? getDefaultValue();

  const state = useState(defaultValue);

  const dispatchStateAsync = useAsyncUpdate((ms, value: T) => {
    setStorageItem(name, value);
    if (ms.isMounted) {
      state[1](value);
    }

    return Promise.resolve();
  });

  const dispatchState = (value: T) => {
    dispatchStateAsync(value);
  };

  return [state[0], dispatchState] as [T, Dispatch<T>];
};
